import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import ReactHighcharts from 'highcharts-react-official';
import HighchartsNoData from 'highcharts/modules/no-data-to-display';
import Highcharts from 'highcharts';
import moment from 'moment-timezone';
import { Unit } from '../../../helpers/unit';

HighchartsNoData(Highcharts)

Highcharts.setOptions({ global: { useUTC: true } });

const colors = {
    titleColor: '#ffffff',
    backgroundColor: '#0000004d',
    primaryColor: '#0051BD',
    secondaryColor: '#ffffff',
    trendColor: "#FF7F00"
};

const FarmGraph = (props) => {
    const { graph, settings } = props;
    const [chart, setChart] = useState(null);
    const unit = Unit.getInstance();

    const [chartOptions, setChartOptions] = useState({
        credits: {
            enabled: false
        },
        exporting: {
            enabled: false
        },
        lang: {
            loading: "Loading...",
            noData: "No Data Found"
        },
        noData: {
            style: {
                fontSize: '15px',
                color: '#aca7a7'
            }
        },
        loading: {
            style: {
                paddingTop: "15px",
                fontSize: '15px',
                color: '#ffffff',
                backgroundColor: "unset"
            }
        },
        chart: {
            type: 'area',
            plotBorderWidth: 0,
            plotBorderColor: colors.primaryColor,
            backgroundColor: colors.backgroundColor,
            borderRadius: "0.25rem",
            events: {
                load: function () {
                    setChart(this)
                }
            }
        },
        title: null,
        plotOptions: {
            area: {
                marker: {
                    enabled: false,
                    symbol: 'none',
                    radius: 0,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                }
            }
        },
        legend: {
            enabled: false,
        },
        xAxis: {
            gridLineWidth: 0.5,
            gridLineColor: colors.primaryColor,
            lineColor: colors.primaryColor,
            type: 'datetime',
            tickPositioner: function () {
                let positions = [],
                    tick = Math.floor(this.dataMin),
                    diff = (this.dataMax - this.dataMin) / (24 * 3600 * 1000),
                    tickSlot = (diff === 1) ? 6 : 4,
                    increment = Math.ceil((this.dataMax - this.dataMin) / tickSlot);

                if (this.dataMax !== null && this.dataMin !== null) {
                    for (tick; tick - increment <= this.dataMax; tick += increment) {
                        positions.push(tick);
                    }
                }
                return positions;
            },
            labels: {
                rotation: -33,
                style: {
                    fontSize: '0.5rem',
                    color: colors.titleColor
                },
                format: "{value:%I %p}",
                formatter: function () {
                    const diff = (this.axis.dataMax - this.axis.dataMin) / (24 * 3600 * 1000);
                    return (diff === 1) ? this.axis.defaultLabelFormatter.call(this) : Highcharts.dateFormat('%e %b %I %p', this.value);
                }
            }
        },
        yAxis: {
            gridLineWidth: 0.5,
            gridLineColor: colors.primaryColor,
            title: {
                text: 'Total Acres',
                style: { color: colors.titleColor, margin: 0 }
            },
            allowDecimals: false,
            labels: {
                rotation: -60,
                style: {
                    fontSize: '0.55rem',
                    color: colors.titleColor,
                },
                formatter: function () { return this.value }
            },
            min: 0
        },
        tooltip: {
            enabled: false,
        },
        series: []
    });

    useEffect(() => {
        setChartOptions({
            ...chartOptions,
            series: []
        });
        const element = document.getElementById("farm-analyse-area-spline-graph");
        if (element && element.children && element.children.length !== 0)
            element.children[0].setAttribute("class", "area-spline-chart");
    }, []);

    useEffect(() => {
        if (graph && graph.isRequesting) {
            for (let i = chart.series.length - 1; i >= 0; i--) {
                if (chart.series[i]) chart.series[i].remove(true);
            }
            chart.hideNoData();
            chart.showLoading();
        }
    }, [graph && graph.isRequesting])

    useEffect(() => {
        bindGraphData();
    }, [graph])

    const bindGraphData = () => {
        if (graph && Object.keys(graph).length > 0 && graph.isRequesting === false) {
            if (chartOptions.series.length > 0 && chartOptions.series[0].length > 0) return;
            let series = [];
            let plannedLine = [];
            let actualLine = [];
            let trendLine = [];

            for (var i = 0; i < graph.planned.length; i++) {
                const acres = unit.toArea(graph.planned[i][1]);
                plannedLine.push([parseUTCTime(graph.planned[i][0]), acres]);
            };

            for (var j = 0; j < graph.actual.length; j++) {
                const acres = unit.toArea(graph.actual[j][1]);
                actualLine.push([parseUTCTime(graph.actual[j][0]), acres]);
            };

            for (var k = 0; k < graph.trend.length; k++) {
                const acres = unit.toArea(graph.trend[k][1]);
                trendLine.push([parseUTCTime(graph.trend[k][0]), acres]);
            };

            series.push({
                fillOpacity: 0.5,
                color: colors.primaryColor,
                lineWidth: 1.5,
                marker: {
                    enabled: false
                },
                data: plannedLine
            });
            series.push({
                fillColor: 'none',
                lineWidth: 1.5,
                lineColor: colors.titleColor,
                marker: {
                    enabled: false
                },
                data: actualLine
            });
            series.push({
                fillColor: 'none',
                lineWidth: 1.5,
                lineColor: colors.trendColor,
                marker: {
                    enabled: false
                },
                data: trendLine
            });

            const plannedAcres = (graph.planned.length > 0) ? graph.planned[graph.planned.length - 1][1] : 0;
            const actualAcres = (graph.actual.length > 0) ? graph.actual[graph.actual.length - 1][1] : 0;
            const trendAcres = (graph.trend.length > 0) ? graph.trend[graph.trend.length - 1][1] : 0;
            const totalAcres = (plannedAcres + actualAcres + trendAcres);

            let yAxisMax = null;
            if (totalAcres === 0) yAxisMax = 10;

            setChartOptions({
                ...chartOptions,
                series: series,
                yAxis: {
                    ...chartOptions.yAxis,
                    max: yAxisMax,
                    text: `Total ${settings.areaShortName}`,
                }
            });
        }
    }

    const parseUTCTime = (dateStr) => {
        return moment.utc(dateStr).valueOf();
    }

    return <ReactHighcharts immutable={true} highcharts={Highcharts} options={chartOptions} />
}

const mapStateToProps = (state) => {
    const selectedPlanTypeId = state.farmMonitor.selectedPlanTypeId;
    const analyseDetails = state.farmMonitor.summaryByPlanTypeId[selectedPlanTypeId] ? state.farmMonitor.summaryByPlanTypeId[selectedPlanTypeId] : null
    return ({
        graph: analyseDetails ? analyseDetails.graph : {},
        settings: state.user.settings
    });
}

export default connect(mapStateToProps)(FarmGraph);